import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "layout";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createVehicle, CreateVehiclePayload } from "services/vehicle";
import { useToast } from "components/Toast";
import VehicleTable from "./VehiclesTable";

interface VehicleFormData {
  vehicleName: string;
  vehicleColor: string;
  vehicleType: string;
  numberOfSeats: number;
  availability: boolean;
  vehicleImage: File | null;
}

const schema = yup.object().shape({
  vehicleName: yup.string().required("Vehicle name is required"),
  vehicleColor: yup.string().required("Vehicle color is required"),
  vehicleType: yup.string().required("Vehicle type is required"),
  numberOfSeats: yup.number().required("Number of seats is required").min(1),
  availability: yup.boolean().required("Availability is required"),
  vehicleImage: yup
    .mixed<File>()
    .required("Vehicle image is required")
    .test(
      "is-file",
      "Vehicle image is required",
      (value) => value instanceof File,
    ),
}) as yup.ObjectSchema<VehicleFormData>;

const AddVehicle = () => {
  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
  } = useForm<VehicleFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      vehicleName: "",
      vehicleColor: "",
      vehicleType: "",
      numberOfSeats: 0,
      availability: true,
      vehicleImage: null,
    },
    mode: "onChange",
  });

  const vehicleMutation = useMutation({
    mutationFn: createVehicle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      showToast("Vehicle added successfully!", "success");
      // Reset form with default values
      reset({
        vehicleName: "",
        vehicleColor: "",
        vehicleType: "",
        numberOfSeats: 0,
        availability: true,
      });
      setImagePreview(null);
      setFileName("");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to add vehicle. Please try again.";
      showToast(errorMessage, "error");
      console.error("Failed to add vehicle:", error);
    },
  });

  const onSubmit = (data: VehicleFormData) => {
    if (!isValid) return;

    const formData: CreateVehiclePayload = {
      name: data.vehicleName,
      type: data.vehicleType,
      availability: data.availability,
      seats: data.numberOfSeats,
      color: data.vehicleColor,
      front_image: data.vehicleImage as File,
    };

    vehicleMutation.mutate(formData);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFileName(file.name);
      setValue("vehicleImage", file, { shouldValidate: true });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <DashboardLayout nextSideBar>
      <Box sx={{ p: { xs: 0, sm: 3 }, bgcolor: "white" }}>
        <Button
          startIcon={<ChevronLeft />}
          onClick={() => navigate(-1)}
          sx={{
            color: "#222",
            textTransform: "none",
            pb: "10px",
            mb: "25px",
            borderBottom: "1px solid #E4E7EC",
            width: "100%",
            justifyContent: "flex-start",
          }}
        >
          Go Back
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <Typography variant="h6" fontWeight="600" sx={{ px: "16px" }}>
            Add New Vehicle
          </Typography>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <FormControl>
              <InputLabel shrink sx={{ color: "#344054", fontWeight: 600 }}>
                Vehicle Name
              </InputLabel>
              <Controller
                name="vehicleName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="BMW M4"
                    fullWidth
                    error={!!errors.vehicleName}
                    helperText={errors.vehicleName?.message}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      mt: 2,
                      bgcolor: "#F9F9F9",
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#98A2B3",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                    }}
                  />
                )}
              />
            </FormControl>

            <FormControl>
              <InputLabel shrink sx={{ color: "#344054", fontWeight: 600 }}>
                Vehicle Color
              </InputLabel>
              <Controller
                name="vehicleColor"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="Blue"
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      mt: 2,
                      bgcolor: "#F9F9F9",
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#98A2B3",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                    }}
                    fullWidth
                    error={!!errors.vehicleColor}
                    helperText={errors.vehicleColor?.message}
                  />
                )}
              />
            </FormControl>

            <FormControl>
              <InputLabel shrink sx={{ color: "#344054", fontWeight: 600 }}>
                Vehicle Type
              </InputLabel>
              <Controller
                name="vehicleType"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="SUV"
                    fullWidth
                    error={!!errors.vehicleType}
                    helperText={errors.vehicleType?.message}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      mt: 2,
                      bgcolor: "#F9F9F9",
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#98A2B3",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                    }}
                  />
                )}
              />
            </FormControl>

            <FormControl>
              <InputLabel shrink sx={{ color: "#344054", fontWeight: 600 }}>
                Availability
              </InputLabel>
              <Controller
                name="availability"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <TextField
                    {...field}
                    select
                    value={value ? "true" : "false"}
                    onChange={(e) => onChange(e.target.value === "true")}
                    SelectProps={{
                      native: true,
                    }}
                    fullWidth
                    error={!!errors.availability}
                    helperText={errors.availability?.message}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      mt: 2,
                      bgcolor: "#F9F9F9",
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#98A2B3",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                      "& select": {
                        px: 2,
                      },
                      "& option": {
                        px: 2,
                        py: 1,
                        borderRadius: "6px",
                      },
                    }}
                  >
                    <option value="true">Available</option>
                    <option value="false">Not Available</option>
                  </TextField>
                )}
              />
            </FormControl>

            <FormControl>
              <InputLabel shrink sx={{ color: "#344054", fontWeight: 600 }}>
                Number of Seats
              </InputLabel>
              <Controller
                name="numberOfSeats"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    placeholder="5"
                    fullWidth
                    error={!!errors.numberOfSeats}
                    helperText={errors.numberOfSeats?.message}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      mt: 2,
                      bgcolor: "#F9F9F9",
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#98A2B3",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                    }}
                  />
                )}
              />
            </FormControl>

            <FormControl error={!!errors.vehicleImage}>
              <InputLabel shrink sx={{ color: "#344054", fontWeight: 600 }}>
                Upload Vehicle Image
              </InputLabel>
              <Box
                sx={{
                  mt: 2,
                  bgcolor: "#F9F9F9",
                  borderRadius: "8px",
                  px: 2,
                  py: 1,
                  cursor: "pointer",
                  border: errors.vehicleImage ? "1px solid #d32f2f" : "none",
                }}
                onClick={() =>
                  document.getElementById("vehicle-image-input")?.click()
                }
              >
                <TextField
                  placeholder="Choose an image"
                  value={fileName || "Choose an image"}
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    readOnly: true,
                    style: {
                      cursor: "pointer",
                    },
                  }}
                  sx={{
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "#98A2B3",
                    width: "100%",
                    cursor: "pointer",
                    "& .MuiInputBase-input": {
                      cursor: "pointer",
                    },
                  }}
                />
                <input
                  id="vehicle-image-input"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                />
              </Box>
              {errors.vehicleImage && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ mt: 1, ml: 2 }}
                >
                  {errors.vehicleImage.message}
                </Typography>
              )}
              {imagePreview && (
                <Box sx={{ mt: 2 }}>
                  <img
                    src={imagePreview}
                    alt="Vehicle preview"
                    style={{ maxWidth: "200px", borderRadius: "8px" }}
                  />
                </Box>
              )}
            </FormControl>

            <Button
              type="submit"
              variant="contained"
              disabled={!isValid || vehicleMutation.isPending}
              sx={{
                mt: 2,
                bgcolor: "#379E66",
                color: "white",
                py: 1,
                fontWeight: 600,
                maxWidth: "300px",
                mx: "auto",
                width: "100%",
                borderRadius: "8px",
                "&:hover": {
                  bgcolor: "#2b7d4f",
                },
              }}
            >
              {vehicleMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </Box>
        </form>
        <Divider sx={{ my: 4 }} />

        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <Typography variant="h6" fontWeight="600" sx={{ px: "16px" }}>
            Available Vehicles
          </Typography>
        </Box>

        <VehicleTable />
      </Box>
      <Box pb={{ xs: 5, sm: 10 }} />
    </DashboardLayout>
  );
};

export default AddVehicle;
